<template>
  <div class="details-dashboard">
    <!-- Header with Date Picker -->
    <div class="details-header">
      <h2 class="details-title">{{ $t('globals.details') || 'Details' }}</h2>
      <div class="header-controls">
        <div class="date-picker-container">
          <label class="date-picker-label">{{ $t('globals.date_range') || 'Date Range' }}:</label>
          <ulu-date-picker
            range
            v-model="range"
            lang="en"
            format="DD.MM.YYYY"
            :placeholder="$t('globals.all')"
            class="modern-date-picker"
          />
        </div>
        <div class="refresh-button" @click="fetchSummaryData">
          <icon name="sync-alt" :spin="isLoadingSummary"></icon>
        </div>
      </div>
    </div>

    <!-- Summary Charts (Doughnut Charts) -->
    <div class="metrics-section">
      <div class="summary-section">
        <div class="charts-grid">
          <!-- Inquiries Chart -->
          <div class="chart-card-small">
            <h4 class="chart-card-title">{{ $t("globals.inquiries") || "Inquiries" }}</h4>
            <div v-if="loading.inquiries" class="loading-state">
              <icon name="spinner" spin class="spinner"></icon>
            </div>
            <div v-else class="chart-content-horizontal">
              <div class="chart-wrapper">
                <ulu-doughnut-chart
                  :chart-data="inquiriesChartData"
                  :options="doughnutOptions"
                />
              </div>
              <div class="chart-legend">
                <div class="legend-item">
                  <span class="legend-color" style="background-color: #48bb78;"></span>
                  <span class="legend-label">Completed</span>
                  <span class="legend-value">{{ inquiries.completed }}</span>
                </div>
                <div class="legend-item">
                  <span class="legend-color" style="background-color: #f56565;"></span>
                  <span class="legend-label">Lost</span>
                  <span class="legend-value">{{ inquiries.lost }}</span>
                </div>
                <div class="legend-item">
                  <span class="legend-color" style="background-color: #ed8936;"></span>
                  <span class="legend-label">Pending</span>
                  <span class="legend-value">{{ inquiries.pending }}</span>
                </div>
                <div class="legend-item total">
                  <span class="legend-label">Ratio</span>
                  <span class="legend-value">{{ inquiries.ratio }}%</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Orders Chart -->
          <div class="chart-card-small">
            <h4 class="chart-card-title">{{ $t("globals.orders") || "Orders" }}</h4>
            <div v-if="loading.orders" class="loading-state">
              <icon name="spinner" spin class="spinner"></icon>
            </div>
            <div v-else class="chart-content-horizontal">
              <div class="chart-wrapper">
                <ulu-doughnut-chart
                  :chart-data="ordersChartData"
                  :options="doughnutOptions"
                />
              </div>
              <div class="chart-legend">
                <div class="legend-item">
                  <span class="legend-color" style="background-color: #48bb78;"></span>
                  <span class="legend-label">Completed</span>
                  <span class="legend-value">{{ orders.completed }}</span>
                </div>
                <div class="legend-item">
                  <span class="legend-color" style="background-color: #f56565;"></span>
                  <span class="legend-label">Terminated</span>
                  <span class="legend-value">{{ orders.terminated }}</span>
                </div>
                <div class="legend-item">
                  <span class="legend-color" style="background-color: #ed8936;"></span>
                  <span class="legend-label">Canceled</span>
                  <span class="legend-value">{{ orders.canceled }}</span>
                </div>
                <div class="legend-item total">
                  <span class="legend-label">Success Rate</span>
                  <span class="legend-value">{{ orders.ratio }}%</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Devices Chart -->
          <div class="chart-card-small">
            <h4 class="chart-card-title">{{ $t("globals.devices") || "Devices" }}</h4>
            <div v-if="loading.devices" class="loading-state">
              <icon name="spinner" spin class="spinner"></icon>
            </div>
            <div v-else class="chart-content-horizontal">
              <div class="chart-wrapper">
                <ulu-doughnut-chart
                  :chart-data="devicesChartData"
                  :options="doughnutOptions"
                />
              </div>
              <div class="chart-legend">
                <div class="legend-item">
                  <span class="legend-color" style="background-color: #48bb78;"></span>
                  <span class="legend-label">Resolved</span>
                  <span class="legend-value">{{ devices.resolved }}</span>
                </div>
                <div class="legend-item">
                  <span class="legend-color" style="background-color: #4299e1;"></span>
                  <span class="legend-label">Warranty</span>
                  <span class="legend-value">{{ devices.warranty }}</span>
                </div>
                <div class="legend-item">
                  <span class="legend-color" style="background-color: #f56565;"></span>
                  <span class="legend-label">Terminated</span>
                  <span class="legend-value">{{ devices.terminated }}</span>
                </div>
                <div class="legend-item">
                  <span class="legend-color" style="background-color: #ed8936;"></span>
                  <span class="legend-label">Canceled</span>
                  <span class="legend-value">{{ devices.canceled }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Invoices Chart -->
          <div class="chart-card-small">
            <h4 class="chart-card-title">{{ $t("globals.invoices") || "Invoices" }}</h4>
            <div v-if="loading.invoices" class="loading-state">
              <icon name="spinner" spin class="spinner"></icon>
            </div>
            <div v-else class="chart-content-horizontal">
              <div class="chart-wrapper">
                <ulu-doughnut-chart
                  :chart-data="invoicesChartData"
                  :options="doughnutOptions"
                />
              </div>
              <div class="chart-legend">
                <div class="legend-item">
                  <span class="legend-color" style="background-color: #48bb78;"></span>
                  <span class="legend-label">Created</span>
                  <span class="legend-value">{{ invoices.created }}</span>
                </div>
                <div class="legend-item">
                  <span class="legend-color" style="background-color: #4299e1;"></span>
                  <span class="legend-label">Scheduled</span>
                  <span class="legend-value">{{ invoices.scheduled }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Customers Card -->
          <div class="chart-card-small">
            <h4 class="chart-card-title">{{ $t("globals.customers") || "Customers" }}</h4>
            <div v-if="loading.customers" class="loading-state">
              <icon name="spinner" spin class="spinner"></icon>
            </div>
            <div v-else class="chart-content-horizontal">
              <div class="chart-wrapper">
                <ulu-doughnut-chart
                  :chart-data="customersChartData"
                  :options="doughnutOptions"
                />
              </div>
              <div class="chart-legend">
                <div class="legend-item">
                  <span class="legend-color" style="background-color: #5469d4;"></span>
                  <span class="legend-label">New Customers</span>
                  <span class="legend-value">{{ customers.new }}</span>
                </div>
                <div class="legend-item total">
                  <span class="legend-label">Total</span>
                  <span class="legend-value">{{ customers.new }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DatePicker from "vue2-datepicker";

import "vue-awesome/icons/spinner";
import "vue-awesome/icons/sync-alt";

export default {
  components: {
    Icon: () => import("vue-awesome/components/Icon.vue"),
    "ulu-date-picker": DatePicker,
    "ulu-doughnut-chart": () => import("@/components/common/charts/DoughnutChart")
  },
  data() {
    return {
      // Date range
      range: [null, null],
      formatedRange: [null, null],

      // Summary charts data
      inquiries: {
        completed: 0,
        lost: 0,
        pending: 0,
        ratio: 0
      },
      orders: {
        completed: 0,
        canceled: 0,
        terminated: 0,
        ratio: 0
      },
      devices: {
        resolved: 0,
        terminated: 0,
        warranty: 0,
        canceled: 0
      },
      invoices: {
        created: 0,
        scheduled: 0
      },
      customers: {
        new: 0
      },
      loading: {
        inquiries: false,
        orders: false,
        devices: false,
        invoices: false,
        customers: false
      }
    };
  },
  computed: {
    ...mapGetters(["getUserSystem"]),

    isLoadingSummary() {
      return (
        this.loading.inquiries ||
        this.loading.orders ||
        this.loading.devices ||
        this.loading.invoices ||
        this.loading.customers
      );
    },

    inquiriesChartData() {
      return {
        labels: ["Completed", "Lost", "Pending"],
        datasets: [
          {
            data: [
              this.inquiries.completed,
              this.inquiries.lost,
              this.inquiries.pending
            ],
            backgroundColor: ["#48bb78", "#f56565", "#ed8936"],
            borderWidth: 2,
            borderColor: "#ffffff"
          }
        ]
      };
    },

    ordersChartData() {
      return {
        labels: ["Completed", "Terminated", "Canceled"],
        datasets: [
          {
            data: [
              this.orders.completed,
              this.orders.terminated,
              this.orders.canceled
            ],
            backgroundColor: ["#48bb78", "#f56565", "#ed8936"],
            borderWidth: 2,
            borderColor: "#ffffff"
          }
        ]
      };
    },

    devicesChartData() {
      return {
        labels: ["Resolved", "Warranty", "Terminated", "Canceled"],
        datasets: [
          {
            data: [
              this.devices.resolved,
              this.devices.warranty,
              this.devices.terminated,
              this.devices.canceled
            ],
            backgroundColor: ["#48bb78", "#4299e1", "#f56565", "#ed8936"],
            borderWidth: 2,
            borderColor: "#ffffff"
          }
        ]
      };
    },

    invoicesChartData() {
      return {
        labels: ["Created", "Scheduled"],
        datasets: [
          {
            data: [this.invoices.created, this.invoices.scheduled],
            backgroundColor: ["#48bb78", "#4299e1"],
            borderWidth: 2,
            borderColor: "#ffffff"
          }
        ]
      };
    },

    customersChartData() {
      return {
        labels: ["New Customers"],
        datasets: [
          {
            data: [this.customers.new || 1],
            backgroundColor: ["#5469d4"],
            borderWidth: 2,
            borderColor: "#ffffff"
          }
        ]
      };
    },

    doughnutOptions() {
      return {
        responsive: true,
        maintainAspectRatio: true,
        cutoutPercentage: 70,
        legend: {
          display: false
        },
        tooltips: {
          enabled: true
        }
      };
    }
  },
  methods: {
    ...mapActions([
      "fetchInquiriesSummary",
      "fetchOrdersSummary",
      "fetchDevicesSummary",
      "fetchInvoicesSummary",
      "fetchCustomersCreatedForRange"
    ]),

    // Summary charts methods
    async fetchInquiriesData() {
      this.loading.inquiries = true;
      try {
        const response = await this.fetchInquiriesSummary();
        if (response.data && response.data.data && response.data.data.inquiriesSummary) {
          const data = JSON.parse(response.data.data.inquiriesSummary);
          this.inquiries = data;
        }
      } catch (error) {
        console.error("Error fetching inquiries summary:", error);
      } finally {
        this.loading.inquiries = false;
      }
    },

    async fetchOrdersData() {
      this.loading.orders = true;
      try {
        const response = await this.fetchOrdersSummary();
        if (response.data && response.data.data && response.data.data.ordersSummary) {
          const data = JSON.parse(response.data.data.ordersSummary);
          this.orders = data;
        }
      } catch (error) {
        console.error("Error fetching orders summary:", error);
      } finally {
        this.loading.orders = false;
      }
    },

    async fetchDevicesData() {
      this.loading.devices = true;
      try {
        const response = await this.fetchDevicesSummary();
        if (response.data && response.data.data && response.data.data.devicesSummary) {
          const data = JSON.parse(response.data.data.devicesSummary);
          this.devices = data;
        }
      } catch (error) {
        console.error("Error fetching devices summary:", error);
      } finally {
        this.loading.devices = false;
      }
    },

    async fetchInvoicesData() {
      this.loading.invoices = true;
      try {
        const response = await this.fetchInvoicesSummary();
        if (response.data && response.data.data && response.data.data.invoicesSummary) {
          const data = JSON.parse(response.data.data.invoicesSummary);
          this.invoices = data;
        }
      } catch (error) {
        console.error("Error fetching invoices summary:", error);
      } finally {
        this.loading.invoices = false;
      }
    },

    async fetchCustomersData() {
      this.loading.customers = true;
      try {
        const response = await this.fetchCustomersCreatedForRange({ range: this.formatedRange });
        if (response.data && response.data.data && response.data.data.noCustomersForRange !== undefined) {
          this.customers.new = response.data.data.noCustomersForRange;
        }
      } catch (error) {
        console.error("Error fetching customers data:", error);
      } finally {
        this.loading.customers = false;
      }
    },

    fetchSummaryData() {
      this.fetchInquiriesData();
      this.fetchOrdersData();
      this.fetchDevicesData();
      this.fetchInvoicesData();
      this.fetchCustomersData();
    }
  },
  watch: {
    range: function(newRange) {
      if (newRange[0] !== null && newRange[1] !== null) {
        let from = new Date(newRange[0]);
        let to = new Date(newRange[1]);

        from.setHours(from.getHours() + 1);
        to.setHours(to.getHours() + 25);

        this.formatedRange = [
          `${from.getFullYear()}-${from.getMonth()+1}-${from.getDate()}`,
          `${to.getFullYear()}-${to.getMonth()+1}-${to.getDate()}`
        ];
      } else {
        this.formatedRange = [null, null];
      }

      // Refetch data when range changes
      this.fetchSummaryData();
    },
    getUserSystem() {
      this.fetchSummaryData();
    }
  },
  mounted() {
    this.fetchSummaryData();
  }
};
</script>

<style scoped>
.details-dashboard {
  padding: 24px;
  background-color: #f7f7f7;
  min-height: calc(100vh - 200px);
}

/* Metrics Section */
.metrics-section {
  margin-bottom: 32px;
}

/* Summary Section */
.summary-section {
  margin-bottom: 32px;
}

.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.summary-title {
  font-size: 20px;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.refresh-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background-color: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  color: #4a5568;
  transition: all 0.2s;
}

.refresh-button:hover {
  background-color: #edf2f7;
  border-color: #5469d4;
  color: #5469d4;
}

.charts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
}

.chart-card-small {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
}

.chart-card-title {
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 16px 0;
  padding-bottom: 12px;
  border-bottom: 2px solid #e2e8f0;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 250px;
  color: #718096;
}

.loading-state .spinner {
  font-size: 32px;
  margin-bottom: 12px;
  color: #5469d4;
}

.loading-state p {
  margin: 0;
  font-size: 14px;
}

.chart-content-horizontal {
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
  flex: 1;
  justify-content: flex-start;
}

.chart-wrapper {
  width: 200px;
  height: 200px;
  position: relative;
  flex-shrink: 0;
}

.chart-legend {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background-color: #f7fafc;
  border-radius: 6px;
  transition: all 0.2s;
}

.legend-item:hover {
  background-color: #edf2f7;
}

.legend-item.total {
  background-color: #edf2f7;
  border-top: 2px solid #e2e8f0;
  margin-top: 4px;
  font-weight: 600;
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  flex-shrink: 0;
}

.legend-label {
  flex: 1;
  font-size: 13px;
  color: #4a5568;
  font-weight: 500;
}

.legend-value {
  font-size: 14px;
  font-weight: 700;
  color: #2d3748;
  min-width: 40px;
  text-align: right;
}

/* Header Section */
.details-header {
  background: white;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.details-title {
  font-size: 24px;
  font-weight: 700;
  color: #2d3748;
  margin: 0;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.date-picker-container {
  display: flex;
  align-items: center;
  gap: 16px;
}

.date-picker-label {
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
  white-space: nowrap;
}

.modern-date-picker {
  max-width: 300px;
}

/* Responsive Styles */
@media (max-width: 1200px) {
  .charts-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .details-dashboard {
    padding: 16px;
  }

  .charts-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .chart-card-small {
    padding: 16px;
  }

  .chart-wrapper {
    width: 200px;
    height: 200px;
  }

  .details-header {
    padding: 16px;
    flex-direction: column;
    align-items: flex-start;
  }

  .header-controls {
    width: 100%;
  }

  .date-picker-container {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    width: 100%;
  }

  .modern-date-picker {
    width: 100%;
    max-width: 100%;
  }
}
</style>
